import { useState } from 'react';
import { AlertCircle, CheckCircle2, XCircle, ShieldCheck } from 'lucide-react';
import { useGetAuditLog, customInstance } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';

const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000000';

const ENTITY_TYPE_LABELS: Record<string, string> = {
  artefact: 'Artefact',
  project: 'Project',
  activity: 'Activity',
  claim: 'Claim',
  time_entry: 'Time Entry',
  tfa_panel: 'TFA Panel',
  tfa_determination: 'TFA Determination',
  tfa_panelist: 'TFA Panelist',
  user: 'User',
  access_token: 'Access Token',
  signal_source: 'Signal Source',
  cost_allocation: 'Cost Allocation',
  grant: 'Grant',
  link: 'Link',
};

function formatEntityType(type: string): string {
  if (ENTITY_TYPE_LABELS[type]) return ENTITY_TYPE_LABELS[type];
  // Fallback: convert snake_case to Title Case
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const ENTITY_TYPES = [
  { value: '', label: 'All entities' },
  { value: 'artefact', label: 'Artefact' },
  { value: 'project', label: 'Project' },
  { value: 'activity', label: 'Activity' },
  { value: 'claim', label: 'Claim' },
  { value: 'time_entry', label: 'Time Entry' },
  { value: 'user', label: 'User' },
  { value: 'access_token', label: 'Access Token' },
];

const ACTION_PREFIXES = [
  { value: '', label: 'All actions' },
  { value: 'artefact.', label: 'artefact.*' },
  { value: 'project.', label: 'project.*' },
  { value: 'activity.', label: 'activity.*' },
  { value: 'claim.', label: 'claim.*' },
  { value: 'time_entry.', label: 'time_entry.*' },
  { value: 'auth.', label: 'auth.*' },
  { value: 'portal.', label: 'portal.*' },
  { value: 'user.', label: 'user.*' },
];

export function AuditLogPage() {
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ valid: boolean; message: string } | null>(null);
  const [entityType, setEntityType] = useState('');
  const [actionPrefix, setActionPrefix] = useState('');

  const { data, isLoading, error, refetch } = useGetAuditLog(
    entityType || actionPrefix
      ? { entityType: entityType || undefined, actionPrefix: actionPrefix || undefined }
      : undefined
  );

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const result = await customInstance<{ data: { valid: boolean; firstBrokenEntryId?: string } }>({
        url: '/api/audit/verify',
        method: 'GET',
      });
      const isValid = result.data?.valid ?? false;
      setVerifyResult({
        valid: isValid,
        message: isValid
          ? 'Hash chain verified — audit log has not been tampered with.'
          : 'Hash chain INVALID — audit log integrity may be compromised. Contact support immediately.',
      });
    } catch {
      setVerifyResult({ valid: false, message: 'Verification failed. Please try again.' });
    } finally {
      setVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 rounded-lg skeleton" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 w-full rounded-lg skeleton" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Failed to load audit log. Please try again.</span>
        <button onClick={() => refetch()} className="ml-auto underline text-xs">Retry</button>
      </div>
    );
  }

  type AuditEntry = NonNullable<NonNullable<typeof data>['data']>['items'] extends (infer T)[] | undefined ? T : never;
  type EnrichedEntry = AuditEntry & { userName?: string | null; userEmail?: string | null };
  const entries = (data?.data?.items ?? []) as EnrichedEntry[];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Immutable record of all state-changing operations. Hash-chained for tamper detection.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => void handleVerify()}
          disabled={verifying}
        >
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          {verifying ? 'Verifying…' : 'Verify chain'}
        </Button>
      </div>

      {verifyResult && (
        <div className="flex items-center gap-3">
          {verifyResult.valid
            ? (
              <Badge variant="positive">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                {verifyResult.message}
              </Badge>
            ) : (
              <div className="flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                <XCircle className="h-4 w-4 shrink-0 mt-1" aria-hidden="true" />
                <span>{verifyResult.message}</span>
              </div>
            )
          }
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          aria-label="Filter by entity type"
          className="rounded-lg border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {ENTITY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <select
          value={actionPrefix}
          onChange={(e) => setActionPrefix(e.target.value)}
          aria-label="Filter by action type"
          className="rounded-lg border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {ACTION_PREFIXES.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>
        {(entityType || actionPrefix) && (
          <button
            onClick={() => { setEntityType(''); setActionPrefix(''); }}
            className="text-xs text-primary hover:underline self-center"
          >
            Clear filters
          </button>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-12 rounded-lg border border-dashed">
          <p className="font-medium">
            {entityType || actionPrefix ? 'No matching audit entries' : 'No audit entries yet'}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {entityType || actionPrefix
              ? 'Try changing the filter to see more entries.'
              : 'Actions performed in Edison will appear here.'}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">Timestamp</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Action</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Entity</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">User</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Hash</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, idx) => (
                <tr key={entry.id} className={`border-b last:border-0 ${idx % 2 === 0 ? '' : 'bg-muted/20'}`}>
                  <td className="px-4 py-3 font-mono tabular-nums text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(entry.timestamp).toISOString().replace('T', ' ').slice(0, 19)}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    <span className="rounded bg-muted px-2 py-0.5">{entry.action}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {entry.entityType && (
                      <span>{formatEntityType(entry.entityType)}{entry.entityId ? `: ${entry.entityId.slice(0, 8)}…` : ''}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {!entry.userId || entry.userId === SYSTEM_USER_ID
                      ? <span className="font-medium text-foreground/60">System</span>
                      : entry.userName ?? entry.userEmail ?? (entry.userId.slice(0, 8) + '…')}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {entry.currentHash ? entry.currentHash.slice(0, 12) + '…' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
